/* Expense > Report Detail — the line-level workspace for one report (Phase 1).
   Wired to GET /v1/expense/reports/:id ({report, lines, total}). An OPEN report can
   add lines (all three types incl. mileage), delete lines, and submit for approval;
   once SUBMITTED/APPROVED/REJECTED the mutating controls disappear (the domain is
   frozen server-side too — this just matches it). Submit surfaces the 422 validation
   violations inline so the owner can fix the offending lines. Export downloads the
   GL-coded CSV. Posting to the ledger happens server-side at approval — never here. */
import {
  ArrowLeftIcon,
  DownloadSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@andavo/web-ui';

import {
  useAddLine,
  useDeleteLine,
  useReportDetail,
  useSubmitReport,
  type ExpenseLine,
  type NewLineRequest,
} from './hooks/use-reports';
import { useAccounts, PL_ACCOUNT_TYPES } from './hooks/use-accounts';
import { useExpenseCoding, CODING_KEYS } from './hooks/use-coding';
import { BRANCH_DEFAULTS } from './reference/branch-defaults';
import { ApiError, expenseFetch } from './lib/expense-api';
import { formatDate, money, reportReference } from './lib/format';
import { LineTypeBadge } from './lineTypeBadge';
import { StatusBadge } from './statusBadge';
import { Field, Input, Select } from './ui/field';

interface Violation {
  lineId: number | null;
  rule: string;
  message: string;
}

function violationsOf(err: unknown): Violation[] {
  if (
    err instanceof ApiError &&
    err.body &&
    typeof err.body === 'object' &&
    'violations' in err.body &&
    Array.isArray((err.body as { violations: unknown }).violations)
  ) {
    return (err.body as { violations: Violation[] }).violations;
  }
  return [];
}

const TODAY = new Date().toISOString().slice(0, 10);

export function ReportDetailScreen() {
  const params = useParams();
  const reportId = Number(params.id);
  const { data, isLoading, isError } = useReportDetail(reportId);
  const addLine = useAddLine(reportId);
  const deleteLine = useDeleteLine(reportId);
  const submit = useSubmitReport();

  const [showAdd, setShowAdd] = useState(false);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <p className="py-10 text-content-secondary text-sm">Loading report…</p>
    );
  }
  if (isError || !data) {
    return (
      <p className="py-10 text-content-danger text-sm">
        Couldn’t load this report.{' '}
        <Link to="/expense/reports" className="text-content-action underline">
          Back to reports
        </Link>
      </p>
    );
  }

  const { report, lines, total } = data;
  const isOpen = report.status === 'OPEN';

  const onSubmit = () => {
    setViolations([]);
    setSubmitError(null);
    submit.mutate(reportId, {
      onError: (err) => {
        const v = violationsOf(err);
        if (v.length > 0) setViolations(v);
        else
          setSubmitError(err instanceof Error ? err.message : 'Submit failed.');
      },
    });
  };

  const onExport = async () => {
    const csv = await expenseFetch<string>(
      `/v1/expense/reports/${reportId}/export`,
    );
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expense-report-${reportId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to="/expense/reports"
          className="inline-flex items-center gap-1.5 font-inter text-content-secondary text-sm hover:text-content-primary"
        >
          <ArrowLeftIcon className="size-icon-sm" /> Reports
        </Link>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h2 className="font-sans font-semibold text-content-primary text-xl">
              {reportReference(report.periodCode, report.reportId)}
            </h2>
            <StatusBadge status={report.status} />
          </div>
          <p className="font-inter text-content-secondary text-sm">
            {report.title ?? report.periodCode} · {lines.length}{' '}
            {lines.length === 1 ? 'line' : 'lines'} · Total{' '}
            <span className="font-semibold text-content-primary">
              {money(total)}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onExport}>
            <DownloadSimpleIcon className="me-2 size-icon-sm" />
            Export CSV
          </Button>
          {isOpen && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAdd((s) => !s)}
              >
                <PlusIcon className="me-2 size-icon-sm" />
                Add line
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={submit.isPending || lines.length === 0}
                onClick={onSubmit}
              >
                Submit for approval
              </Button>
            </>
          )}
        </div>
      </div>

      {violations.length > 0 && (
        <Card className="border-button-danger-default bg-surface-danger-subtle p-4">
          <p className="font-inter font-semibold text-content-danger text-sm">
            This report can’t be submitted yet:
          </p>
          <ul className="mt-2 list-disc space-y-1 ps-5 font-inter text-content-danger text-sm">
            {violations.map((v, i) => (
              <li key={`${v.rule}-${v.lineId ?? 'report'}-${i}`}>
                {v.message}
                {v.lineId != null && (
                  <span className="text-content-secondary">
                    {' '}
                    (line {v.lineId})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {submitError && (
        <p className="font-inter text-content-danger text-sm">{submitError}</p>
      )}

      {showAdd && isOpen && (
        <AddLineForm
          pending={addLine.isPending}
          onCancel={() => setShowAdd(false)}
          onAdd={(line) =>
            addLine.mutate(line, { onSuccess: () => setShowAdd(false) })
          }
        />
      )}

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Vendor / purpose</TableHead>
              <TableHead>GL</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Posting</TableHead>
              {isOpen && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => (
              <LineRow
                key={line.lineId}
                line={line}
                canDelete={isOpen}
                deleting={deleteLine.isPending}
                onDelete={() => deleteLine.mutate(line.lineId)}
              />
            ))}
            {lines.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={isOpen ? 7 : 6}
                  className="py-10 text-center text-content-secondary"
                >
                  No lines yet.{isOpen ? ' Add one to get started.' : ''}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function LineRow({
  line,
  canDelete,
  deleting,
  onDelete,
}: {
  line: ExpenseLine;
  canDelete: boolean;
  deleting: boolean;
  onDelete: () => void;
}) {
  return (
    <TableRow data-line-row>
      <TableCell>
        <LineTypeBadge type={line.lineType} />
      </TableCell>
      <TableCell className="text-content-secondary">
        {formatDate(line.txnDate)}
      </TableCell>
      <TableCell className="text-content-primary">
        {line.vendor ?? line.description ?? '—'}
      </TableCell>
      <TableCell className="text-content-secondary">
        {line.glAccountId ?? '—'}
      </TableCell>
      <TableCell className="text-right font-inter font-semibold text-content-primary">
        {money(line.amount)}
      </TableCell>
      <TableCell>
        {line.postingJournalEntryId != null ? (
          <span className="font-inter text-content-success text-xs">
            Posted
          </span>
        ) : (
          <span className="font-inter text-content-secondary text-xs">
            Not posted
          </span>
        )}
      </TableCell>
      {canDelete && (
        <TableCell className="text-right">
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            aria-label={`Delete line ${line.lineId}`}
            className="text-content-secondary transition-colors hover:text-content-danger disabled:opacity-50"
          >
            <TrashIcon className="size-icon-sm" />
          </button>
        </TableCell>
      )}
    </TableRow>
  );
}

type LineType = ExpenseLine['lineType'];

function AddLineForm({
  pending,
  onAdd,
  onCancel,
}: {
  pending: boolean;
  onAdd: (line: NewLineRequest) => void;
  onCancel: () => void;
}) {
  const [lineType, setLineType] = useState<LineType>('CARD_CHARGE');
  const [txnDate, setTxnDate] = useState(TODAY);
  const [amount, setAmount] = useState('');
  const [vendor, setVendor] = useState('');
  const [description, setDescription] = useState('');
  const [glAccountId, setGlAccountId] = useState('');
  const [branch, setBranch] = useState('');
  const [productGroup, setProductGroup] = useState('');
  const [supplier, setSupplier] = useState('');
  const [miles, setMiles] = useState('');

  const isMileage = lineType === 'MILEAGE';

  // Dropdown sources: GL accounts (filtered to P&L — what an expense codes to) +
  // the branch / product-group / supplier COA attribute values (seeded from Agresso).
  const { data: accounts = [] } = useAccounts();
  const plAccounts = accounts.filter((a) =>
    (PL_ACCOUNT_TYPES as readonly string[]).includes(a.accountType),
  );
  const coding = useExpenseCoding();

  // Pick a branch → auto-fill its product group (1:1 off branch in Agresso). The user
  // can still override the product group afterward.
  const onBranchChange = (value: string) => {
    setBranch(value);
    const def = BRANCH_DEFAULTS[value];
    if (def) setProductGroup(def.productGroup);
  };

  const handle = (e: FormEvent) => {
    e.preventDefault();
    // Coding dimensions ride in the line's attribute_values JSONB (validated server-side
    // against coa_attribute at submit). Only include the ones actually chosen.
    const attrs: Record<string, string> = {};
    if (branch) attrs[CODING_KEYS.branch] = branch;
    if (productGroup) attrs[CODING_KEYS.productGroup] = productGroup;
    if (supplier) attrs[CODING_KEYS.supplier] = supplier;
    const base: NewLineRequest = {
      lineType,
      currency: 'USD',
      txnDate,
      vendor: vendor || undefined,
      description: description || undefined,
      glAccountId: glAccountId ? Number(glAccountId) : undefined,
      attributeValuesJson:
        Object.keys(attrs).length > 0 ? JSON.stringify(attrs) : undefined,
    };
    if (isMileage) {
      onAdd({ ...base, mileageMiles: miles, mileageRateEffective: txnDate });
    } else {
      onAdd({ ...base, amount });
    }
  };

  return (
    <Card className="p-5">
      <form onSubmit={handle} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Type" htmlFor="line-type">
            <Select
              id="line-type"
              value={lineType}
              onChange={(e) => setLineType(e.target.value as LineType)}
            >
              <option value="CARD_CHARGE">Card charge</option>
              <option value="OUT_OF_POCKET">Out of pocket</option>
              <option value="MILEAGE">Mileage</option>
            </Select>
          </Field>

          <Field label="Date" htmlFor="line-date">
            <Input
              id="line-date"
              type="date"
              value={txnDate}
              onChange={(e) => setTxnDate(e.target.value)}
              required
            />
          </Field>

          {isMileage ? (
            <Field
              label="Miles"
              htmlFor="line-miles"
              hint="Reimbursement = miles × the IRS rate (computed at save)."
            >
              <Input
                id="line-miles"
                type="number"
                step="0.1"
                min="0"
                value={miles}
                onChange={(e) => setMiles(e.target.value)}
                required
              />
            </Field>
          ) : (
            <Field label="Amount (USD)" htmlFor="line-amount">
              <Input
                id="line-amount"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </Field>
          )}

          {!isMileage && (
            <Field label="Vendor" htmlFor="line-vendor">
              <Input
                id="line-vendor"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="e.g. Delta Air Lines"
              />
            </Field>
          )}

          <Field label="Purpose / description" htmlFor="line-desc">
            <Input
              id="line-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Business purpose"
            />
          </Field>

          <Field label="GL account" htmlFor="line-gl">
            <Select
              id="line-gl"
              value={glAccountId}
              onChange={(e) => setGlAccountId(e.target.value)}
            >
              <option value="">Select account…</option>
              {plAccounts.map((a) => (
                <option key={a.accountId} value={String(a.accountId)}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Branch"
            htmlFor="line-branch"
            hint="Sets the product group automatically."
          >
            <Select
              id="line-branch"
              value={branch}
              onChange={(e) => onBranchChange(e.target.value)}
            >
              <option value="">Select branch…</option>
              {coding.branch.map((b) => (
                <option key={b.valueId} value={b.value}>
                  {b.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Product group" htmlFor="line-pg">
            <Select
              id="line-pg"
              value={productGroup}
              onChange={(e) => setProductGroup(e.target.value)}
            >
              <option value="">Select product group…</option>
              {coding.productGroup.map((p) => (
                <option key={p.valueId} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Supplier (AP code)" htmlFor="line-supplier">
            <Select
              id="line-supplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            >
              <option value="">Select supplier…</option>
              {coding.supplier.map((s) => (
                <option key={s.valueId} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={pending}>
            Add line
          </Button>
        </div>
      </form>
    </Card>
  );
}
